import type { ApolloError } from '@apollo/client';

import { CAREER_HISTORY } from 'src/components/Router';
import { REJECT, DELETE } from 'src/consts';
import { isGraphQLAuthError } from 'src/modules/error-handling/error-handling';
import { ERROR } from 'src/modules/syncing';
import type { ReduxInjections } from 'src/types';
import * as Schema from 'src/types/graphql';

import { Actions, type CareerHistoryAction } from './actions';
import { getUnconfirmedRolesFromCache } from './data';

export const updateCareerHistory = (
  newRole: Omit<Schema.UpdateRoleInput, 'function' | 'industry'>,
  action: string,
): CareerHistoryAction => ({
  type: Actions.UPDATE_CAREER_HISTORY_START,
  payload: newRole,
  actionName: action,
});

export const updateCareerHistorySuccess = (): CareerHistoryAction => ({
  type: Actions.UPDATE_CAREER_HISTORY_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const updateCareerHistoryFailure = (
  error: ApolloError,
): CareerHistoryAction => ({
  type: Actions.UPDATE_CAREER_HISTORY_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const addUnconfirmedRole = (
  id: { id: string },
  action: string,
  contentRead: boolean = true,
): CareerHistoryAction => ({
  type: Actions.ADD_UNCONFIRMED_ROLE_START,
  payload: id,
  actionName: action,
  meta: {
    metrics: {
      action,
    },
    analytics: {
      contentRead,
    },
  },
});

export const addUnconfirmedRoleSuccess = (): CareerHistoryAction => ({
  type: Actions.ADD_UNCONFIRMED_ROLE_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const addUnconfirmedRoleFailure = (
  error: ApolloError,
): CareerHistoryAction => ({
  type: Actions.ADD_UNCONFIRMED_ROLE_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const rejectUnconfirmedRole =
  (role: Schema.RejectUnconfirmedDataInput) =>
  ({
    dispatch,
    api,
    appConfig: { showFeature },
  }: ReduxInjections<CareerHistoryAction>) => {
    const { unconfirmedRoles } = getUnconfirmedRolesFromCache(
      api.apolloClient,
      showFeature('resume-parsing'),
    );
    const { id, tracking = null } =
      unconfirmedRoles.find((unconfirmed) => unconfirmed.id === role.id) || {};

    if (id === undefined) {
      throw new TypeError('The role to reject could not be found');
    }

    dispatch({
      type: Actions.REJECT_UNCONFIRMED_ROLE_START,
      actionName: REJECT,
      payload: {
        dataType: Schema.UnconfirmedDataType.CareerHistory,
        id,
        tracking,
      },
    });
  };

export const rejectUnconfirmedRoleSuccess = (): CareerHistoryAction => ({
  type: Actions.REJECT_UNCONFIRMED_ROLE_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const rejectUnconfirmedRoleFailure = (
  error: ApolloError,
): CareerHistoryAction => ({
  type: Actions.REJECT_UNCONFIRMED_ROLE_FAILURE,
  payload: error,
  meta: {
    sync: {
      state: ERROR,
      parent: CAREER_HISTORY,
      error,
    },
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const deleteRole = (): CareerHistoryAction => ({
  type: Actions.DELETE_CAREER_HISTORY_START,
  actionName: DELETE,
});

export const deleteRoleSuccess = (): CareerHistoryAction => ({
  type: Actions.DELETE_CAREER_HISTORY_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const deleteRoleFailure = (error: ApolloError): CareerHistoryAction => ({
  type: Actions.DELETE_CAREER_HISTORY_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});
